import * as React from 'react';

import { Menu } from '@headlessui/react';

import { clsx } from '@freelancelabs/utils';

export interface DropdownMenuItemProps {
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    /**
     * A gesture that takes something away: archiving, revoking. It is the row that reads
     * as destructive, never the whole menu.
     */
    tone?: 'default' | 'destructive';
    className?: string;
}

/**
 * A row of an actions menu.
 *
 * **It exists because `Dropdown` alone does not close.** Its only consumer until now was a
 * radio group of locales, where staying open while the reader picks is right; an actions
 * menu is the opposite, and a plain `<button>` inside `Menu.Items` gets neither the
 * keyboard handling nor the close on activation. `Menu.Item` gives both, and nothing else
 * in the package wrapped it.
 *
 * The look comes from the back-office mockup's `.menu button`: a full-width row, 10/12
 * padding, a soft hover, and a destructive variant that tints text and hover rather than
 * painting a red block.
 */
const DropdownMenuItem = ({ children, onClick, disabled, tone = 'default', className }: DropdownMenuItemProps) => (
    <Menu.Item disabled={disabled}>
        {({ active }) => (
            <button
                type="button"
                onClick={onClick}
                disabled={disabled}
                className={clsx(
                    'dropdown-menu-item',
                    tone === 'destructive' && 'dropdown-menu-item--destructive',
                    active && 'is-active',
                    className
                )}
            >
                {children}
            </button>
        )}
    </Menu.Item>
);

export default DropdownMenuItem;
